import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import DemoImage from './DemoImage';
import OptimizedImage from './OptimizedImage';
import * as DemoImageActions from '../actions/DemoImageActions';
import {applicationModes} from '../constants';
import cloudinary from 'cloudinary-core';

let optimizedImagesIds = ['baseline', 'optimized1', 'optimized2', 'optimized3'];

let imageDescriptors = {
  default: {
    baseline: {
      transformation: { quality: 90, width: 'auto', aspect_ratio: '270:300', dpr: 'auto', crop: 'fill' },
      title: 'JPEG - 90% Quality',
      format: 'jpg',
      description: '<p>Set the JPEG compression quality to 90%.</p><p>The visual quality is nearly the same as the original, but the file size is quite a bit smaller.</p>'
    },
    optimized1: {
      transformation: { quality: 70, width: 'auto', aspect_ratio: '270:300', dpr: 'auto', crop: 'fill' },
      title: 'JPEG - 70% Quality',
      format: 'jpg',
      description: '<p>Set the JPEG compression quality to 70%.</p><p>The visual quality is a bit lower the original, but the file size is significantly smaller.</p>'
    },
    optimized2: {
      transformation: { quality: 70, width: 'auto', aspect_ratio: '270:300', dpr: 'auto', crop: 'fill', fetch_format: 'auto' },
      title: 'WebP -  70% Quality',
      format: 'webp',
      description: '<p>Set the WebP compression quality to 70%.</p><p>The visual quality is a bit lower the original, but the file size is significantly smaller.</p>'
    },
    optimized3: {
      transformation: { quality: 70, width: 'auto', aspect_ratio: '270:300', dpr: 'auto', crop: 'fill', fetch_format: 'auto' },
      title: 'JPEG-XR - 70% Quality',
      format: 'wdp',
      description: '<p>Set the JPEG-XR compression quality to 70%.</p><p>The visual quality is a bit lower the original, but the file size is significantly smaller.</p>'
    },
    main_optimized: {
      description: '<p>Optimize the image by reducing its quality to 70%.</p><p>The visual quality is a bit lower the original, but the file size is significantly smaller.</p>',
      transformation: { quality: 70, fetch_format: 'auto' },
      display_transformation: { dpr: 'auto', width: 'auto', aspect_ratio: '370:247', crop: 'fill', quality: 70, fetch_format: 'auto' }
    }

  },
  qa: {
    baseline: {
      transformation: { quality: 90, width: 'auto', aspect_ratio: '270:300', dpr: 'auto', crop: 'fill' },
      title: 'JPEG - 90% Quality',
      format: 'jpg',
      description: '<p>Set the JPEG compression quality to 90%.</p><p>The visual quality is nearly the same as the original, but the file size is quite a bit smaller.</p>'
    },
    optimized1: {
      transformation: { quality: 'auto', width: 'auto', aspect_ratio: '270:300', dpr: 'auto', crop: 'fill' },
      title: 'JPEG - Auto Quality',
      format: 'jpg',
      description: '<p>Deliver a JPEG image in optimal quality.</p><ul><li>Analyze the image content</li><li>Tune the encoding settings and compression level</li><li>Balance intelligently between visual quality and file size</li></ul>'
    },
    optimized2: {
      transformation: { quality: 'auto', width: 'auto', aspect_ratio: '270:300', dpr: 'auto', crop: 'fill', fetch_format: 'auto' },
      title: 'WebP -  Auto Quality',
      format: 'webp',
      description: '<p>Deliver a WebP image in optimal quality.</p><ul><li>Analyze the image content</li><li>Tune the encoding settings and compression level</li><li>Balance intelligently between visual quality and file size</li></ul>'
    },
    optimized3: {
      transformation: { quality: 'auto:eco', width: 'auto', aspect_ratio: '270:300', dpr: 'auto', crop: 'fill' },
      title: 'JPEG - Auto Quality - Eco',
      format: 'jpg',
      description: '<p>Deliver a JPEG image in economic quality.</p><ul><li>Analyze the image content</li><li>Adjust the encoding settings and compression level</li><li>Favor bandwidth savings for fast image delivery</li></ul>'
    },
    main_optimized: {
      description: '<p>Deliver the optimal image in the optimal format according to the viewing browser.</p><ul><li>Analyze the image content</li><li>Balance intelligently between visual quality, file format and file size</li></ul>',
      transformation: { quality: 'auto', fetch_format: 'auto' },
      display_transformation: { dpr: 'auto', width: 'auto', aspect_ratio: '370:247', crop: 'fill', quality: 'auto', fetch_format: 'auto' }
    }
  }
};

class OptimizationPanel extends Component {
  getSize(name) {
    const imageInfo = this.props.demoImages[name];
    if (imageInfo) {
      return imageInfo.size;
    } else {
      return 0;
    }
  }

  constructor(props, context) {
    super(props, context);
    this.handleHiddenComplete = this.handleHiddenComplete.bind(this);
    this.state = { hiddenSrc: null };
    this.cl = props.cl;
    if (!this.cl) {
      this.cl = cloudinary.Cloudinary.new();
      this.cl.fromDocument();
    }
  }

  handleHiddenComplete(e) {
    this.setState({...this.state, hiddenSrc: e.target.src});
  }

  render() {
    const actions = this.props.actions;
    if (this.props.originalSize) {
      let originalSize = Math.round(this.props.originalSize / 1024);
      let optimizedSize = this.getSize('optimizedFullsize');
      let optimizedSizeKB = Math.round(optimizedSize / 1024);
      let optimizationPercent = Math.round(100 * (1 - optimizedSize / this.props.originalSize));
      let desc = imageDescriptors[this.props.applicationMode] || imageDescriptors[applicationModes.DEFAULT];
      let optimizedTransformation = desc.main_optimized;
      let optimizedAnchorUrl = this.state.hiddenSrc;
      let anchorUrl = this.cl.url(this.props.publicId);

      return (
        <div className="container">
          <section className="well img-optimization">
            <h2 className="text-center">Optimizing and adapting your images</h2>
            <div className="img-comparison">
              <div className="col">
                <div className="caption">
                  <span className="title">Original <span className="hidden-phone">image</span></span>
                  {originalSize > 0 ? <strong className="size">{originalSize}KB</strong>: null}
                </div>
                <DemoImage
                  publicId={this.props.publicId}
                  className="img"
                  transformation={{ dpr: 'auto', width: 'auto', aspect_ratio: '370:247', crop: 'fill' }}
                  format="jpg"
                  showDetails={actions.showDetails}
                  anchorUrl={anchorUrl} >
                  <p>The original uploaded image</p>
                </DemoImage>
              </div>
              <div className="col">
                <span className="icon-arrow-right" />
              </div>
              <div className="col">
                <div className="caption">

                  {optimizedSizeKB > 0 ?
                  <div className="wrap-l">
                    <span className="title">Optimized <span className="hidden-phone">image</span></span>
                    <strong className="size">{optimizedSizeKB}KB</strong>
                  </div> :
                  <div className="wrap-l">
                    <span className="title">Optimized <span className="hidden-phone">image</span></span>
                    <strong className="size">Loading...</strong>
                  </div>
                  }

                  {(optimizationPercent > 0 && optimizationPercent < 100) ?
                  (<div className="wrap-r">
                    <strong>{optimizationPercent}%</strong>
                    <sub>optimization</sub>
                    </div>) : <div className="wrap-r"/>
                  }
                </div>
                <DemoImage publicId={this.props.publicId} id="optimizedFullsize" className="hidden" transformation={desc.main_optimized.transformation} format="jpg" handleComplete={this.handleHiddenComplete} {...actions}/>
                <DemoImage publicId={this.props.publicId} className="img" height={247} width={370} transformation={desc.main_optimized.display_transformation} format="jpg" showDetails={actions.showDetails} anchorUrl={optimizedAnchorUrl} >
                  <div dangerouslySetInnerHTML={{ __html: desc.main_optimized.description}} />
                </DemoImage>
              </div>
            </div>
            <h3 className="text-center">Optimized manipulated images</h3>
            <ul className="list-inline img-list">
              {optimizedImagesIds.map((id, i) =>
                <OptimizedImage key={i} publicId={this.props.publicId} id={id} transformation={desc[id].transformation} format={desc[id].format} title={desc[id].title} {...actions} size={this.getSize(id)} baselineSize={this.getSize('baseline')} hideSaving={id === 'baseline'}>
                   <div dangerouslySetInnerHTML={{ __html: desc[id].description}} />
                </OptimizedImage>
              )}
            </ul>
          </section>
        </div>
      );
    } else {
      return null;
    }
  }
}

function mapStateToProps(state) {
  let bytes;
  if (state.currentImage) {
    bytes = state.currentImage.bytes;
  }
  return {
    originalSize: bytes,
    demoImages: state.demoImages
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(DemoImageActions, dispatch)
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OptimizationPanel);



/** WEBPACK FOOTER **
 ** ./src/js/components/OptimizationPanel.js
 **/